/**
 * Functions for comparing viewmodel entities (rows, groups) with study entities, and for deriving RE-related metadata
 * @module
 */
import * as _ from 'lodash'
import { isRapidExtract, rowTime, tryParseQuantifier, inferRowTypes, termSetsMatch } from 'services/extract/util'
import { Extraction, DAStudy, DAStudyGroup, DAStudyRow, DACell, EGroup, ERow } from 'services/extract/types'
import { compareGroupDosages, toDosage } from 'util/quantifier'
import { post } from 'tools/request'
import { batched } from 'util/batched'
import { StringGrouped } from 'types/util'
import StudyContext from './StudyContext'
import { STUDY_LEVEL_GRP } from 'services/extract/types'
import { lookupRowConfig } from 'services/extract/generate'

const DRE_CLIENT_ID = 3

export const enum REC_STATE {
	FORBIDDEN = -1,
	MISSING = 0,
	DOCDATA,
	RAPID_EXTRACT
}

function isExtractableRowType(section, rowType, fieldType, valueType?, lt?, name?) {
	return !!lookupRowConfig(section, fieldType, rowType, valueType, lt, name)
}

export function isExtractableRow(section: string, row: DAStudyRow) {
	const { rowType, fieldType } = inferRowTypes(row)
	return isExtractableRowType(section, rowType, fieldType, row.dataType,
		_.isNumber(row.name) ? row.name : undefined,
		_.isString(row.name) ? row.name : undefined)
}

const remoteCompareQuantifiers = batched(
	async lists => post('/portal/comparequantifiers', { data: lists }),
	(list, result, queued) => result[queued.indexOf(list)]
)
const remoteNormalizeInterval = batched(
	timepoints => post('/portal/normitvl', { intervals: timepoints }),
	(item, result, queued) => result[queued.indexOf(item)]
)

export async function groupsEqual(studyGroup: DAStudyGroup, extractGroup: EGroup, compareQuantifiers = remoteCompareQuantifiers) {
	if (studyGroup.Id == extractGroup.id) return true
	if (extractGroup.existing) {
		// if this is an explicitly selected/loaded group, it matches only the selected group and no others
		return studyGroup.Id == extractGroup.existing
	}
	if (studyGroup.Interventions?.length != extractGroup.interventions?.length) return
	if (!(studyGroup.Interventions?.length > 0)) return
	// check for match of any alternative term sets
	const studyItvns = []
	const extractItvns = []
	const remaining = extractGroup.interventions.slice()
	for (const itvn of studyGroup.Interventions || []) {
		const sets = (itvn.termIds || []).map(set => set.join('/'))
		const match = remaining.find(i =>
			(itvn.name && i.name?.term?.label && itvn.name == i.name?.term?.label) ||
			termSetsMatch(sets, i.name?.term?.senses)
		)
		if (!match) return false
		studyItvns.push(itvn)
		extractItvns.push(match)
		_.pull(remaining, match)
	}
	// all interventions are matched by terms, compare respective dosages between matches
	const studyDosages = studyItvns.map(itvn => (itvn.Treatments || []).map(t => t.Dosages || []))
	const extractDosages = extractItvns.map(itvn =>
		(itvn.treatments || []).map(t =>
			(t.measure?.val || '').trim()
		).filter(m => m).map(str => {
			const q = tryParseQuantifier(str)
			return q ? [toDosage(q)] : []
		})
	)
	return await compareGroupDosages(studyDosages, extractDosages, compareQuantifiers)
}

export function rowsEqual(studyRow: DAStudyRow, extractRow: ERow, extractInterval) {
	if (studyRow.id == extractRow.id) return true
	if (extractRow.existing) {
		// if this is an explicitly selected/loaded row, it matches only the selected row and no others
		return studyRow.id == extractRow.existing
	}
	const extractSenses = _.compact(extractRow.name?.term?.senses)
	const sameTime = (extractInterval?.days == null && studyRow.time?.days == null)
		|| Math.trunc(extractInterval?.days) == Math.trunc(studyRow.time?.days)
	const sets = (studyRow.termIds || []).map(set => set.join('/'))
	const rowTypes = inferRowTypes(studyRow)
	const extractRowTypes = { rowType: extractRow.rowType, fieldType: extractRow.type }
	return sameTime && rowTypes.rowType == extractRowTypes.rowType && rowTypes.fieldType == extractRowTypes.fieldType &&
		((studyRow.name && extractRow.name?.term?.label && studyRow.name == extractRow.name?.term?.label) ||
			termSetsMatch(sets, extractSenses))
}

/**
 * if the record was created by a user in the same client, or by a doctor evidence user/client
 * then it will be visible by any other user account in that client
 */
function visibleToClient(clientId, recordClientId) {
	return [undefined, DRE_CLIENT_ID, clientId].includes(recordClientId)
}

export function extractibility(section: string, group: DAStudyGroup, row: DAStudyRow, cell: DACell) {
	const groupScore = +!!group + +isRapidExtract(group)
	const rowScore = +!!row + +isRapidExtract(row)
	const cellScore = +!!cell + +isRapidExtract(cell)
	// prohibit editing existing docdata cells
	if (row && !isExtractableRow(section, row)) return -1
	if (groupScore == REC_STATE.DOCDATA && rowScore == REC_STATE.DOCDATA && cellScore == REC_STATE.DOCDATA) return -1
	if (groupScore == REC_STATE.MISSING && rowScore == REC_STATE.DOCDATA
		/* && cellScore By definition a cell cannot exist for a non-existent group*/) return -1
	return groupScore << 4 | rowScore << 2 | cellScore
}

interface CellEntry {
	group: DAStudyGroup
	row: DAStudyRow
	cell: DACell
}
type CellLookup = (rowId, grpId) => CellEntry[]
export function indexCells(groups: DAStudyGroup[], rows: DAStudyRow[]): CellLookup {
	const studyGroupsById = _.keyBy(groups, g => g?.Id)
	const cellsByRowId = _.groupBy(rows.flatMap(row => (row.cells || []).map(cell => {
		return {
			group: cell.group ? studyGroupsById[cell.group] : STUDY_LEVEL_GRP,
			row,
			cell
		}
	})), c => c.row.id)
	return (rowId, grpId) => cellsByRowId[rowId]?.filter(e => e.cell.group == grpId)
}

export const getComparisons = (groups: DAStudyGroup[], outcomeRows: DAStudyRow[], clientId: number, studyAccessible: boolean) => {
	const groupComparisons = _.compact(groups.map(group => evalGroup(group, clientId, studyAccessible)))
	const rowComparisons = _.compact(outcomeRows.map(row => evalRow('Outcomes', row, clientId, studyAccessible)))
	const cellCoords = toCellCoords(rowComparisons, groupComparisons)
	const cellMap = indexCells(groups, outcomeRows)
	return _.compact(cellCoords.map(coord => compareCell(coord, cellMap, clientId, studyAccessible)))
}

const toGroupMeta = (group: DAStudyGroup, clientId: number, studyAccessible: boolean) => {
	if (group == null) {
		// the implicit "study level" group coord
		return {
			match: null,
			isRE: false,
			isExtractable: true,
			isAccessible: true,
			isReferenceable: true,
			isEditable: true,
			score: REC_STATE.DOCDATA
		}
	}
	return entityMeta(group, clientId, studyAccessible)
}

const toRowMeta = (section, row: DAStudyRow, clientId: number, studyAccessible: boolean) =>
	entityMeta(row, clientId, studyAccessible, row => isExtractableRow(section, row))

const toCellMeta = (cell: DACell, clientId: number, studyAccessible: boolean) => ({
	...entityMeta(cell, clientId, studyAccessible),
	isEditable: true
})

const toMatchMeta = <E, S>(extracted: E, meta: EntityMeta<S>) => ({
	extracted,
	...meta
})

const getGroupMatches = (group: EGroup, studyGroups: DAStudyGroup[], compareQuantifiers = remoteCompareQuantifiers) =>
	getEntityMatches(group, studyGroups, (group, studyGroup) =>
		groupsEqual(studyGroup, group, compareQuantifiers)
	)

const getRowMatches = async (row: ERow, studyRows: DAStudyRow[], normalizeInterval = remoteNormalizeInterval) => {
	let itvl = rowTime(row)
	if (itvl) itvl = await normalizeInterval(itvl)
	return getEntityMatches(row, studyRows,
			async (row, studyRow) => rowsEqual(studyRow, row, itvl)
	)
}
export const compareGroup = async (group: EGroup, studyGroups: DAStudyGroup[], clientId: number, studyAccessible: boolean, compareQuantifiers = remoteCompareQuantifiers) => {
	const matches = await getGroupMatches(group, studyGroups, compareQuantifiers)
	return compareGroupMatches(group, matches, clientId, studyAccessible)
}

export const compareRow = async (row: ERow, studyRows: DAStudyRow[], clientId: number, studyAccessible: boolean, normalizeInterval = remoteNormalizeInterval) => {
	const matches = await getRowMatches(row, studyRows, normalizeInterval)
	return compareRowMatches(row.section, row, matches, clientId, studyAccessible)
}

export const evalGroup = (group: DAStudyGroup, clientId, studyAccessible: boolean) =>
	compareGroupMatches(group, [group], clientId, studyAccessible)

export const evalRow = (section: string, row: DAStudyRow, clientId, studyAccessible: boolean) =>
	compareRowMatches(section, row, [row], clientId, studyAccessible)

const compareGroupMatches = <T>(group: T, matchingGroups: DAStudyGroup[], clientId: number, studyAccessible: boolean) =>
	toEntityComparison(matchingGroups.map(match => toMatchMeta(group, toGroupMeta(match, clientId, studyAccessible))))

const compareRowMatches = <T>(section: string, row: T, matchingRows: DAStudyRow[], clientId: number, studyAccessible: boolean) =>
	toEntityComparison(matchingRows.map(match => toMatchMeta(row, toRowMeta(section, match, clientId, studyAccessible))))

type RowComparison<T = ERow> = EntityComparison<T, DAStudyRow>
type GroupComparison<T = EGroup> = EntityComparison<T, DAStudyGroup>
interface CellCoord<R = ERow, G = EGroup> {
	row: R
	rowMatch: RowComparison<R>
	group: G
	groupMatch: GroupComparison<G>
}

const mergeScores = (cellMatch, rowMatch, groupMatch) => {
	const scores = [rowMatch, groupMatch, cellMatch]
	if (scores.some(m => !m?.isAccessible)) return REC_STATE.FORBIDDEN
	const score = (m => m?.isRE ? REC_STATE.RAPID_EXTRACT : REC_STATE.DOCDATA)
	return score(rowMatch) << 4 | score(groupMatch) << 4 | score(cellMatch)
}

function compareCell<R, G>(coord: CellCoord<R, G>, cellMap: CellLookup, clientId: number, studyAccessible: boolean): EntityComparison<CellCoord<R, G>, CellEntry> {
	const { row, rowMatch: rowComp, group, groupMatch: groupComp } = coord
	const matches = rowComp.matches.flatMap(rowMatch =>
		groupComp.matches.flatMap(groupMatch => {
			let cells = (cellMap(rowMatch.match.id, groupMatch.match?.Id) || [])
			if (!cells.length) cells = [undefined]
			return cells.flatMap(cell => {
				const cellMatch = cell?.cell && toMatchMeta(coord, toCellMeta(cell.cell, clientId, studyAccessible))
				const isRE = cellMatch?.isRE ? cellMatch.isRE : (rowMatch.isRE || groupMatch.isRE)
				// extraction of this cell coordinate is supported if it is supported for the row and group match
				const isExtractable = _.every([cellMatch, rowMatch, groupMatch], meta => !meta || meta.isExtractable)
				// this cell is accessible for edit if the row and group matches are accessible
				const isAccessible =  _.every([cellMatch, rowMatch, groupMatch], meta => !meta || meta.isAccessible)
				const isEditable = rowMatch.isReferenceable && groupMatch.isReferenceable &&
					(/* either the cell itself is RE or is in a RE row/group coord */
						(!cellMatch || cellMatch.isEditable) ||
						(rowMatch.isEditable || groupMatch.isEditable)
					)
				const score = mergeScores(cellMatch, rowMatch, groupMatch)
				return {
					extracted: coord,
					match: cell,
					isRE,
					isExtractable,
					isAccessible,
					isReferenceable: false,
					isEditable,
					score
				}
			})
		})
	)
	return toEntityComparison(matches)
}

const toCellCoords = <R, G>(rowMatches: RowComparison<R>[], groupMatches: GroupComparison<G>[]) =>
	(rowMatches || []).flatMap(rowMatch => {
		return (groupMatches || []).map(groupMatch => ({
			row: rowMatch.extracted,
			rowMatch,
			group: groupMatch.extracted,
			groupMatch
		}))
	})

async function compareExtractedEntities(study: StudyContext, extraction: Extraction, normalizeInterval = remoteNormalizeInterval, compareQuantifiers = remoteCompareQuantifiers) {
	const clientId = study.clientId
	const groups = study.groups
	// find matching groups
	const nullGroup = compareGroupMatches(null, [null], clientId, study.isAccessible)
	const groupComparisons = _.compact(await Promise.all(extraction.groups.map(group =>
		compareGroup(group, groups, clientId, study.isAccessible, compareQuantifiers)
	)))

	// find matching rows
	const studyLevelComparisons = _.compact(await Promise.all(_.toArray(extraction.studyLevel).map(row =>
		compareRow(row, study.studyLevel, clientId, study.isAccessible, normalizeInterval)
	)))
	const groupLevelComparisons = _.compact(await Promise.all(_.toArray(extraction.groupLevel).map(row =>
		compareRow(row, study.groupLevel, clientId, study.isAccessible, normalizeInterval)
	)))
	const characteristicsComparisons = _.compact(await Promise.all(_.toArray(extraction.characteristics).map(row =>
		compareRow(row, study.characteristics, clientId, study.isAccessible, normalizeInterval)
	)))
	const outcomeComparisons = _.compact(await Promise.all(_.toArray(extraction.outcomes).map(row =>
		compareRow(row, study.outcomes, clientId, study.isAccessible, normalizeInterval)
	)))
	const rowComparisons = [...outcomeComparisons, ...studyLevelComparisons, ...groupLevelComparisons, ...characteristicsComparisons]
	// for each extracted group and outcome, find overlaps with existing group/outcomes
	const cellCoords = toCellCoords(outcomeComparisons, groupComparisons.concat(nullGroup))
	const cellComparisons = _.compact(cellCoords.map(coord => compareCell(coord, study.indexedCells, clientId, study.isAccessible)))
	return {
		groupComparisons,
		studyLevelComparisons,
		groupLevelComparisons,
		characteristicsComparisons,
		outcomeComparisons,
		rowComparisons,
		cellMap: study.indexedCells,
		cellComparisons
	}
}

export interface ExtractionComparison {
	groupMap: StringGrouped<DAStudyGroup>
	rowMap: StringGrouped<DAStudyRow>
	comparisons: (EntityComparison<CellCoord, CellEntry> & CellCoord & {
		matchingGroups: DAStudyGroup[]
		matchingRows: DAStudyRow[]
		matchingCells: CellEntry[]
	})[]
}

export async function compareExtraction(study: StudyContext, extraction: Extraction, normalizeInterval = remoteNormalizeInterval, compareQuantifiers = remoteCompareQuantifiers): Promise<ExtractionComparison> {
	const { groupComparisons, rowComparisons, cellComparisons } = await compareExtractedEntities(study, extraction, normalizeInterval, compareQuantifiers)
	const mapMatches = <E extends ERow | EGroup, S>(comparisons: EntityComparison<E, S>[]) =>
		_.transform(comparisons, (obj, comparison) => obj[comparison.extracted?.id] = comparison.matches.map(m => m.match), {} as StringGrouped<S>)
	const groupMap: StringGrouped<DAStudyGroup> = mapMatches(groupComparisons)
	const rowMap: StringGrouped<DAStudyRow> = mapMatches(rowComparisons)
	const comparisons =  Array.from(cellComparisons).map(comparison => ({
		...comparison,
		...comparison.extracted,
		matchingGroups: comparison.extracted.groupMatch?.matches.map(m => m.match),
		matchingRows: comparison.extracted.rowMatch?.matches.map(m => m.match),
		matchingCells: comparison.matches.filter(m => m.match).map(m => m.match)
	}))
	return {
		groupMap,
		rowMap,
		comparisons
	}
}

export function studyIsAccessible(study, user) {
	// has to work for both DA study and DA-full study, so inspect private flag, available on both
	if (!study?.private) return true
	const permit = study.metadata?.Permit
	if (permit) {
		const userId = Number(/^user:(\d+)$/.exec(permit)?.[1])
		const teamId = Number(/^team:(\d+)$/.exec(permit)?.[1])
		const clientId = Number(/^client:(\d+)$/.exec(permit)?.[1])
		if (userId) {
			return userId == user.Id
		} else if (teamId) {
			return (user.Teams || []).concat(user.AllTeams || []).some(t => t.Id == teamId)
		} else if (clientId) {
			return user.Client.Id == clientId
		}
	} else {
		return user.Client.Id == DRE_CLIENT_ID || user.Client.Id == study.metadata?.Extractor?.Client?.Id
	}
}

const isAccessibleDefault = (studyAccessible: boolean, entity, clientId) => studyAccessible

interface EntityMeta<E> {
	match: E
	isRE: boolean
	isExtractable: boolean
	isAccessible: boolean
	isReferenceable: boolean
	isEditable: boolean
	score: number
}
export function entityMeta<E>(entity: E, clientId: number, studyAccessible: boolean, isExtractionSupported = (entity: E) => true, isAccessibleFn = isAccessibleDefault): EntityMeta<E> {
	const isRE = isRapidExtract(entity)
	const isExtractable = !entity.isAutomatedImport && isExtractionSupported(entity)
	const isAccessible = isAccessibleFn(studyAccessible, entity, clientId)
	const isReferenceable = isAccessible
	const isEditable = isReferenceable
	return {
		match: entity,
		isRE,
		isExtractable,
		isAccessible,
		isReferenceable,
		isEditable,
		score: !isAccessible ? REC_STATE.FORBIDDEN : (isRE ? REC_STATE.RAPID_EXTRACT : REC_STATE.DOCDATA)
	}
}

interface EntityMatch<E, S> extends EntityMeta<S> {
	extracted: E
}

export interface EntityComparison<E, S> {
	extracted: E,
	matches: EntityMatch<E, S>[]
	editable: EntityMatch<E, S>
	referenceable: EntityMatch<E, S>
	uneditable: boolean
	unextractable: EntityMatch<E, S>[]
	inaccessible: EntityMatch<E, S>[]
}

async function getEntityMatches<E, S>(extracted: E, sources: S[], isEqual: (e: E, s: S) => Promise<boolean>) {
	const matches: S[] = []
	for (const srcEntity of sources) {
		if (await isEqual(extracted, srcEntity)) {
			matches.push(srcEntity)
		}
	}
	return matches
}

const toEntityComparison = <E, S>(matches: EntityMatch<E, S>[]): EntityComparison<E, S> => {
	if (!matches?.length) return
	const metas = _.sortBy(matches, ({score}) => -score)
	const accessible = metas.filter(m => m.isAccessible)
	const referenceable = accessible.find(m => m.isReferenceable)
	const editable = accessible.find(m => m.isEditable)
	return {
		extracted: matches[0].extracted,
		matches: metas,
		editable,
		referenceable,
		uneditable: !!matches.length && !editable,
		unextractable: matches.filter(({isExtractable}) => !isExtractable),
		inaccessible: matches.filter(({isAccessible}) => !isAccessible)
	}
}

const comparisonMap = <E, S>(comparisons: EntityComparison<E, S>[]) =>
	_.transform(_.compact(comparisons), (result, comparison) => {
		result.set(comparison.extracted, comparison)
	}, new Map<E, EntityComparison<E, S>>())

export function detectOverwrite({ studyLevel, groupLevel, outcomes, characteristics, groups }: Extraction, comparison: ExtractionComparison) {
	const cellComparisons = []
	const extractedRows = _.concat(studyLevel, groupLevel, outcomes, characteristics)
	for (const comp of comparison.comparisons) {
		const { row, group } = comp
		if (extractedRows.some(er => er.id == row.id) && group == null || groups.some(eg => (eg == null && group == null) || eg.id == group.id)) {
			cellComparisons.push(comp)
		}
	}
	// any of the cells we are adding/editing already exist
	return cellComparisons.some(c => c.matches.some(m => m.match))
}

export function detectInvalidNewGroups(groups: EGroup[], studyCtx: StudyContext, groupMap: ExtractionComparison['groupMap']): EGroup[] {
	if (!studyCtx.canCreateGroups) {
		return groups.filter(g => !groupMap[g.id]?.length)
	}
}